import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Mail, MapPin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interest: 'Security & Surveillance',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Thanks — we\'ll get back to you shortly.');
    setIsSubmitting(false);
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      interest: 'Security & Surveillance',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <h1 className="heading-1 mb-6">Request a demo—or start a discovery conversation.</h1>
            <p className="body-large text-[var(--text-secondary)] max-w-2xl mx-auto">
              Tell us what you're trying to achieve. We'll recommend the right deployment model and the fastest path to results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-body)] mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[var(--border-light)] rounded-lg bg-[var(--bg-card)] text-[var(--text-body)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-body)] mb-1">Company *</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[var(--border-light)] rounded-lg bg-[var(--bg-card)] text-[var(--text-body)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] transition-all"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-body)] mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[var(--border-light)] rounded-lg bg-[var(--bg-card)] text-[var(--text-body)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] transition-all"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-body)] mb-1">Phone (optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[var(--border-light)] rounded-lg bg-[var(--bg-card)] text-[var(--text-body)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] transition-all"
                    placeholder="+61"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-body)] mb-1">Area of Interest *</label>
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[var(--border-light)] rounded-lg bg-[var(--bg-card)] text-[var(--text-body)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] transition-all"
                  >
                    <option>Security & Surveillance</option>
                    <option>Industry 4.0</option>
                    <option>Agriculture</option>
                    <option>Partnership</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-body)] mb-1">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-[var(--border-light)] rounded-lg bg-[var(--bg-card)] text-[var(--text-body)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] transition-all resize-none"
                    placeholder="Tell us about your environment..."
                  />
                </div>
                <p className="text-xs text-[var(--text-muted)]">
                  By submitting this form, you agree to be contacted by Ferntree about your enquiry.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full"
                >
                  {isSubmitting ? 'Sending...' : 'Send Request'}
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="space-y-8">
              <div>
                <h3 className="heading-3 mb-6">Get in touch</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium text-[var(--text-primary)]">Location</p>
                      <p className="text-[var(--text-secondary)]">Sydney, Australia</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium text-[var(--text-primary)]">Email</p>
                      <a href="mailto:hello@ferntree.io" className="text-[var(--accent-primary)] hover:underline">hello@ferntree.io</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg">
                <h4 className="font-semibold text-[var(--text-primary)] mb-3">What happens next?</h4>
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] flex items-center justify-center text-xs font-medium flex-shrink-0">1</span>
                    <span>We'll review your enquiry and respond within 24 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] flex items-center justify-center text-xs font-medium flex-shrink-0">2</span>
                    <span>Schedule a discovery call to discuss your environment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] flex items-center justify-center text-xs font-medium flex-shrink-0">3</span>
                    <span>Receive a tailored recommendation and deployment plan</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;